import React, { useState, useEffect, useMemo } from 'react';
import { UserPermission, GenerationTask } from '../types';
import { getAllUserPermissions, saveUserPermission, deleteUserPermission } from '../services/storageService';
import { Trash2, Plus, Save, Edit, X, Shield, User as UserIcon, AlertTriangle } from 'lucide-react';

interface ChannelManagementProps {
  onBack: () => void;
  availableAgents: string[];
  tasks: GenerationTask[];
  onBulkDelete: (taskIds: string[]) => Promise<void>;
}

export const ChannelManagement: React.FC<ChannelManagementProps> = ({ 
  onBack, 
  availableAgents,
  tasks,
  onBulkDelete
}) => {
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBulkDeletePanel, setShowBulkDeletePanel] = useState(false);
  const [selectedBulkDates, setSelectedBulkDates] = useState<Set<string>>(new Set());
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  const datesGroupInfo = useMemo(() => {
    if (tasks.length === 0) return [];
    
    const groups: Record<string, { date: string, count: number, taskIds: string[] }> = {};
    tasks.forEach(task => {
      const date = new Date(task.createdAt);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const dateKey = `${y}-${m}-${d}`;
      
      if (!groups[dateKey]) {
        groups[dateKey] = { date: dateKey, count: 0, taskIds: [] };
      }
      groups[dateKey].count++;
      groups[dateKey].taskIds.push(task.id);
    });
    
    return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
  }, [tasks]);

  const handleBulkDateDelete = async () => {
    console.log("[ChannelManagement] handleBulkDateDelete triggered");
    if (selectedBulkDates.size === 0) {
      console.warn("[ChannelManagement] No dates selected");
      return;
    }
    
    const selectedGroups = datesGroupInfo.filter(group => selectedBulkDates.has(group.date));
    const selectedTaskIds = selectedGroups.flatMap(group => group.taskIds);
    const totalCount = selectedTaskIds.length;
    
    console.log(`[ChannelManagement] Calculated ${totalCount} tasks across ${selectedBulkDates.size} dates`);
      
    if (isDeletingBulk) {
      console.warn("[ChannelManagement] Already deleting, skipping request");
      return;
    }

    setIsDeletingBulk(true);
    try {
      console.log("[ChannelManagement] Calling onBulkDelete...");
      // Add a small delay for visual feedback of button state change
      await new Promise(resolve => setTimeout(resolve, 300));
      await onBulkDelete(selectedTaskIds);
      console.log("[ChannelManagement] onBulkDelete promise fulfilled");
      
      alert(`선택한 날짜의 문서 ${totalCount}개가 삭제되었습니다.`);
      setSelectedBulkDates(new Set());
      setConfirmMode(false);
      setShowBulkDeletePanel(false);
    } catch (err) {
      console.error("[ChannelManagement] Bulk delete process failed:", err);
      // alert already handled in App.tsx or we handle it here
    } finally {
      setIsDeletingBulk(false);
      console.log("[ChannelManagement] isDeletingBulk reset to false");
    }
  };

  const [confirmMode, setConfirmMode] = useState(false);
  const totalTasksToExclude = useMemo(() => {
    return datesGroupInfo
      .filter(group => selectedBulkDates.has(group.date))
      .reduce((sum, g) => sum + g.count, 0);
  }, [selectedBulkDates, datesGroupInfo]);

  const toggleBulkDate = (date: string) => {
    const next = new Set(selectedBulkDates);
    if (next.has(date)) next.delete(date);
    else next.add(date);
    setSelectedBulkDates(next);
  };
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    setLoading(true);
    const data = await getAllUserPermissions();
    setPermissions(data);
    setLoading(false);
  };

  const handleSave = async (email: string) => {
    if (!email || !email.includes('@')) {
      alert("올바른 이메일 형식을 입력해주세요.");
      return;
    }

    try {
      const permission: UserPermission = {
        id: email,
        email,
        allowedAgents: selectedAgents,
        isAdmin,
        updatedAt: Date.now()
      };
      await saveUserPermission(permission);
      alert("권한이 저장되었습니다.");
      setEditingEmail(null);
      setNewEmail('');
      setSelectedAgents([]);
      setIsAdmin(false);
      loadPermissions();
    } catch (err) {
      console.error("[ChannelManagement] Save failed:", err);
      alert("권한 저장 중 오류가 발생했습니다.");
    }
  };

  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);

  const handleDelete = async (email: string) => {
    console.log("[ChannelManagement] handleDelete started for:", email);
    try {
      await deleteUserPermission(email);
      console.log("[ChannelManagement] deleteUserPermission success for:", email);
      setDeletingEmail(null);
      loadPermissions();
    } catch (err) {
      console.error("[ChannelManagement] Permission delete failed:", err);
      const errMsg = err instanceof Error ? err.message : String(err);
      alert(`권한 삭제 중 오류가 발생했습니다: ${errMsg}`);
      setDeletingEmail(null);
    }
  };

  const startEditing = (p: UserPermission) => {
    setEditingEmail(p.email);
    setNewEmail(p.email);
    setSelectedAgents(p.allowedAgents);
    setIsAdmin(p.isAdmin);
  };

  const toggleAgent = (agent: string) => {
    if (agent === 'all') {
      setSelectedAgents(['all']);
    } else {
      const filtered = selectedAgents.filter(a => a !== 'all');
      if (filtered.includes(agent)) {
        setSelectedAgents(filtered.filter(a => a !== agent));
      } else {
        setSelectedAgents([...filtered, agent]);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-black text-slate-900">채널 및 권한 관리</h1>
            <p className="text-slate-500 font-medium">사용자별 접근 가능한 에이전트를 설정합니다.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                setShowBulkDeletePanel(!showBulkDeletePanel);
                setConfirmMode(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all border ${
                showBulkDeletePanel 
                  ? 'bg-red-600 border-red-600 text-white' 
                  : 'bg-white border-slate-200 text-red-600 hover:bg-red-50'
              }`}
            >
              <Trash2 size={16} />
              <span>날짜 선택 삭제</span>
            </button>
            <button 
              onClick={onBack}
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              뒤로가기
            </button>
          </div>
        </div>

        {showBulkDeletePanel && (
          <div className="bg-white rounded-2xl shadow-lg border-2 border-red-100 overflow-hidden mb-8 animate-in slide-in-from-top-4 duration-200">
            <div className="p-6 border-b border-red-50 bg-red-50/30 flex justify-between items-center">
              <div>
                <h2 className="text-sm font-black text-red-900 uppercase tracking-wider flex items-center gap-2">
                  <AlertTriangle size={16} />
                  날짜별 문서 일괄 삭제
                </h2>
                <p className="text-xs text-red-600/70 font-bold mt-1">삭제할 날짜를 선택하세요. 선택된 날짜의 모든 문서가 영구 삭제됩니다.</p>
              </div>
              <button 
                onClick={() => setShowBulkDeletePanel(false)}
                className="p-2 hover:bg-red-100 rounded-lg text-red-400 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-slate-50/50 rounded-xl border border-slate-100 p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[320px] overflow-y-auto pr-2 custom-scrollbar transition-all">
                  {datesGroupInfo.length === 0 ? (
                    <div className="col-span-full py-10 text-center text-slate-400 font-bold">문서 데이터가 없습니다.</div>
                  ) : (
                    datesGroupInfo.map(group => (
                      <label 
                        key={group.date}
                        className={`flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedBulkDates.has(group.date)
                            ? 'border-red-500 bg-white shadow-sm ring-4 ring-red-500/5'
                            : 'border-white bg-white hover:border-red-200 shadow-sm'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <input 
                            type="checkbox"
                            checked={selectedBulkDates.has(group.date)}
                            onChange={() => toggleBulkDate(group.date)}
                            className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer"
                          />
                          <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                            {group.count}
                          </span>
                        </div>
                        <span className={`text-sm font-black ${selectedBulkDates.has(group.date) ? 'text-red-700' : 'text-slate-700'}`}>
                          {group.date}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              
              <div className="mt-8 flex justify-between items-center py-4 border-t border-slate-100">
                <div className="text-sm font-bold text-slate-500">
                  선택됨: <span className="text-red-600 font-black">{selectedBulkDates.size}</span> 개 날짜 
                  {totalTasksToExclude > 0 && <span className="ml-1">(총 {totalTasksToExclude}개 문서)</span>}
                </div>
                <div className="flex gap-3">
                  {!confirmMode ? (
                    <>
                      <button 
                        onClick={() => {
                          if (selectedBulkDates.size === datesGroupInfo.length) {
                            setSelectedBulkDates(new Set());
                          } else {
                            setSelectedBulkDates(new Set(datesGroupInfo.map(d => d.date)));
                          }
                        }}
                        className="px-4 py-2 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 transition-all"
                      >
                        {selectedBulkDates.size === datesGroupInfo.length ? "전체 해제" : "전체 선택"}
                      </button>
                      <button 
                        onClick={() => {
                          if (selectedBulkDates.size > 0) setConfirmMode(true);
                          else alert("삭제할 날짜를 먼저 선택해주세요.");
                        }}
                        disabled={selectedBulkDates.size === 0 || isDeletingBulk}
                        className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-all shadow-lg shadow-red-200"
                      >
                        <Trash2 size={16} />
                        <span>삭제 단계로 이동</span>
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-4 bg-red-50 p-2 pl-4 rounded-xl border border-red-200">
                      <span className="text-xs font-black text-red-700">정말 모두 삭제할까요? 되돌릴 수 없습니다.</span>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setConfirmMode(false)}
                          disabled={isDeletingBulk}
                          className="px-4 py-2 bg-white text-slate-600 rounded-lg text-xs font-bold border border-slate-200 hover:bg-slate-50"
                        >
                          취소
                        </button>
                        <button 
                          onClick={handleBulkDateDelete}
                          disabled={isDeletingBulk}
                          className="px-6 py-2 bg-red-600 text-white rounded-lg text-xs font-black hover:bg-red-700 flex items-center gap-2 shadow-lg shadow-red-100 disabled:opacity-50"
                        >
                          {isDeletingBulk ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              <span>삭제 중...</span>
                            </>
                          ) : (
                            "데이터 영구 삭제 실행"
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8">
          <div className="p-6 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Plus size={16} className="text-blue-600" />
              새 권한 추가 / 수정
            </h2>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">사용자 이메일</label>
                <input 
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="example@gmail.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-medium"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-3 cursor-pointer select-none group">
                  <input 
                    type="checkbox"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-bold text-slate-700 group-hover:text-blue-600 transition-colors flex items-center gap-1.5">
                    <Shield size={16} />
                    관리자 권한 부여
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">허용할 에이전트 선택</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleAgent('all')}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                    selectedAgents.includes('all')
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400'
                  }`}
                >
                  전체 허용 (All)
                </button>
                {availableAgents.map(agent => (
                  <button
                    key={agent}
                    onClick={() => toggleAgent(agent)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                      selectedAgents.includes(agent)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400'
                    }`}
                  >
                    {agent}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              {editingEmail && (
                <button 
                  onClick={() => {
                    setEditingEmail(null);
                    setNewEmail('');
                    setSelectedAgents([]);
                    setIsAdmin(false);
                  }}
                  className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-50 transition-all flex items-center gap-2"
                >
                  <X size={18} />
                  취소
                </button>
              )}
              <button 
                onClick={() => handleSave(newEmail)}
                disabled={!newEmail}
                className="px-8 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200 flex items-center gap-2"
              >
                <Save size={18} />
                {editingEmail ? '수정 사항 저장' : '권한 추가하기'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider">권한 목록</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">사용자</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">허용된 에이전트</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">관리자</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-right">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-slate-400 font-medium">로딩 중...</td>
                  </tr>
                ) : permissions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-slate-400 font-medium">설정된 권한이 없습니다.</td>
                  </tr>
                ) : (
                  permissions.map(p => (
                    <tr key={p.email} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                            <UserIcon size={16} />
                          </div>
                          <span className="text-sm font-bold text-slate-700">{p.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {p.allowedAgents.includes('all') ? (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-black uppercase">All</span>
                          ) : (
                            p.allowedAgents.map(a => (
                              <span key={a} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold">{a}</span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {p.isAdmin ? (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-[10px] font-black uppercase flex items-center gap-1 w-fit">
                            <Shield size={10} />
                            Admin
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-400">일반</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {deletingEmail === p.email ? (
                            <div className="flex items-center gap-1 animate-in fade-in slide-in-from-right-1">
                              <button 
                                onClick={() => handleDelete(p.email)}
                                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-[10px] font-black hover:bg-red-700 shadow-sm transition-all"
                              >
                                정말 삭제?
                              </button>
                              <button 
                                onClick={() => setDeletingEmail(null)}
                                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                                title="취소"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <button 
                                onClick={() => startEditing(p)}
                                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex items-center gap-1"
                                title="수정"
                              >
                                <Edit size={16} />
                                <span className="text-xs font-bold">수정</span>
                              </button>
                              <button 
                                onClick={() => setDeletingEmail(p.email)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center gap-1"
                                title="삭제"
                              >
                                <Trash2 size={16} />
                                <span className="text-xs font-bold">삭제</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
